import { Link, createFileRoute } from '@tanstack/react-router';
import {
  ArrowDownRight,
  ArrowLeftRight,
  ArrowRight,
  BookOpenText,
  Gauge,
  LineChart,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cx } from '@/lib/format';

export const Route = createFileRoute('/')({ component: Landing });

const linkButton =
  'press inline-flex items-center justify-center rounded-control font-medium transition-colors';

/**
 * The landing page shows the product's actual point of view: a market rate only
 * becomes useful after it is compared with the visitor's own cost basis.
 */
function Landing() {
  const { t } = useTranslation();

  return (
    <div className="landing-shell min-h-dvh overflow-hidden bg-[var(--bg)]">
      <header className="relative z-10 mx-auto flex w-full max-w-6xl items-center justify-between px-5 py-5 sm:px-8 sm:py-7">
        <Link to="/" className="flex items-center gap-2.5" aria-label={t('common.appNameFull')}>
          <span className="brand-mark flex size-9 items-center justify-center rounded-[11px] bg-[var(--accent)] text-white shadow-sm">
            <ArrowLeftRight size={18} strokeWidth={2.35} />
          </span>
          <span className="flex items-baseline gap-2">
            <span className="text-[20px] font-bold tracking-[-0.035em]">Cambio</span>
            <span className="text-[13px] font-medium text-secondary">/ 汇记</span>
          </span>
        </Link>
        <nav className="flex items-center gap-2" aria-label={t('landing.accountNav')}>
          <Link
            to="/login"
            className={cx(
              linkButton,
              'h-9 border border-[var(--separator)] bg-[var(--bg-elevated)] px-4 text-[15px] text-primary shadow-sm',
            )}
          >
            {t('auth.signIn')}
          </Link>
          <Link
            to="/register"
            className={cx(
              linkButton,
              'h-9 bg-[var(--accent)] px-4 text-[15px] text-white shadow-[0_7px_18px_rgb(180_35_27_/_0.18)]',
            )}
          >
            {t('auth.signUp')}
          </Link>
        </nav>
      </header>

      <main>
        <section className="relative mx-auto grid w-full max-w-6xl gap-12 px-5 pb-20 pt-12 sm:px-8 sm:pt-20 lg:grid-cols-[0.92fr_1.08fr] lg:items-center lg:gap-16 lg:pb-28 lg:pt-24">
          <div className="relative z-[1]">
            <p className="mb-5 flex items-center gap-2 text-[13px] font-semibold tracking-[0.08em] text-secondary">
              <span className="h-px w-7 bg-[var(--text-secondary)]" aria-hidden />
              {t('landing.eyebrow')}
            </p>
            <h1 className="max-w-[650px] text-balance text-[42px] font-bold leading-[1.08] tracking-[-0.045em] sm:text-[60px]">
              {t('landing.tagline')}
            </h1>
            <p className="mt-5 max-w-lg text-pretty text-[18px] leading-relaxed text-secondary sm:text-[21px]">
              {t('landing.subline')}
            </p>

            <div className="mt-9 flex items-center">
              <Link
                to="/login"
                search={{ demo: true }}
                className={cx(
                  linkButton,
                  'h-12 gap-2 bg-[var(--accent)] px-6 text-[17px] text-white shadow-[0_10px_28px_rgb(180_35_27_/_0.24)]',
                )}
              >
                {t('landing.viewDemo')}
                <ArrowRight size={18} aria-hidden />
              </Link>
            </div>
            <p className="mt-4 flex items-center gap-2 text-[13px] text-tertiary">
              <span className="size-1.5 rounded-full bg-[var(--accent)]" aria-hidden />
              {t('auth.demoHint')}
            </p>
          </div>

          <RateDesk />
        </section>

        <section className="border-y border-[var(--separator)] bg-[var(--bg-elevated)]">
          <div className="mx-auto grid w-full max-w-6xl px-5 sm:px-8 lg:grid-cols-[0.62fr_1.38fr]">
            <div className="flex flex-col justify-between border-b border-[var(--separator)] py-10 lg:border-b-0 lg:border-r lg:py-14 lg:pr-14">
              <div>
                <p className="text-[13px] font-semibold tracking-[0.08em] text-secondary">
                  {t('landing.workflowKicker')}
                </p>
                <h2 className="mt-3 max-w-sm text-balance text-[30px] font-bold leading-tight tracking-[-0.035em] sm:text-[38px]">
                  {t('landing.workflowTitle')}
                </h2>
              </div>
              <p className="mt-6 max-w-xs text-[15px] leading-relaxed text-secondary">
                {t('landing.workflowBody')}
              </p>
            </div>

            <div className="lg:pl-14">
              <FeatureRow
                icon={<BookOpenText size={20} />}
                step="01"
                title={t('landing.featureCostTitle')}
                body={t('landing.featureCostBody')}
              />
              <FeatureRow
                icon={<Gauge size={20} />}
                step="02"
                title={t('landing.featureDecisionTitle')}
                body={t('landing.featureDecisionBody')}
              />
              <FeatureRow
                icon={<LineChart size={20} />}
                step="03"
                title={t('landing.featureTimingTitle')}
                body={t('landing.featureTimingBody')}
              />
            </div>
          </div>
        </section>

        <footer className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-5 py-10 sm:px-8 sm:py-12 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-baseline gap-2">
            <span className="text-[16px] font-bold tracking-[-0.025em]">Cambio</span>
            <span className="text-[12px] text-tertiary">{t('landing.footerLabel')}</span>
          </div>
          <ul className="flex flex-col gap-3 text-[13px] text-secondary sm:flex-row sm:items-center sm:gap-0">
            <FooterFact>{t('landing.footerCurrencies')}</FooterFact>
            <FooterFact>{t('landing.footerPortable')}</FooterFact>
          </ul>
        </footer>
      </main>
    </div>
  );
}

function FooterFact({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-center gap-3 sm:gap-0 sm:before:mx-4 sm:before:block sm:before:size-1 sm:before:rounded-full sm:before:bg-[var(--separator)] sm:first:before:hidden">
      <span className="size-1 rounded-full bg-[var(--separator)] sm:hidden" aria-hidden />
      {children}
    </li>
  );
}

function RateDesk() {
  const { t } = useTranslation();

  return (
    <div className="relative mx-auto w-full max-w-[590px]" aria-label={t('landing.previewLabel')}>
      <div
        className="absolute -inset-20 -z-10 rounded-full bg-[radial-gradient(circle,var(--fill-2)_0%,transparent_67%)]"
        aria-hidden
      />
      <div className="overflow-hidden rounded-[28px] border border-[var(--separator)] bg-[var(--bg-elevated)] shadow-[var(--shadow-raised)]">
        <div className="flex items-center justify-between border-b border-[var(--separator)] px-5 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <span className="flex size-10 items-center justify-center rounded-full bg-[var(--fill-3)] text-[13px] font-bold">
              A$
            </span>
            <div>
              <p className="text-[15px] font-semibold">AUD / CNY</p>
              <p className="text-[12px] text-secondary">{t('landing.previewHistory')}</p>
            </div>
          </div>
          <span className="rounded-full bg-[color-mix(in_srgb,var(--positive)_12%,transparent)] px-3 py-1 text-[12px] font-semibold text-[var(--positive)]">
            {t('common.today')}
          </span>
        </div>

        <div className="grid gap-0 sm:grid-cols-2">
          <div className="border-b border-[var(--separator)] p-5 sm:border-b-0 sm:border-r sm:p-6">
            <p className="text-[12px] font-medium text-secondary">{t('landing.previewAverage')}</p>
            <p className="tnum mt-1 font-mono text-[34px] font-semibold tracking-[-0.05em]">
              4.8432
            </p>
            <p className="mt-1 text-[12px] text-tertiary">{t('landing.previewBasis')}</p>
          </div>
          <div className="p-5 sm:p-6">
            <p className="text-[12px] font-medium text-secondary">{t('landing.previewMarket')}</p>
            <div className="mt-1 flex items-baseline gap-2">
              <p className="tnum font-mono text-[34px] font-semibold tracking-[-0.05em]">4.7126</p>
              <span className="flex items-center text-[13px] font-semibold text-[var(--positive)]">
                <ArrowDownRight size={14} aria-hidden />
                2.7%
              </span>
            </div>
            <p className="mt-1 text-[12px] text-tertiary">{t('landing.previewUpdated')}</p>
          </div>
        </div>

        <div className="landing-rate-plot relative h-40 border-y border-[var(--separator)] bg-[var(--bg-inset)] px-5 py-4 sm:h-48 sm:px-6">
          <div className="absolute inset-x-6 top-[44%] flex items-center gap-2" aria-hidden>
            <span className="h-px flex-1 border-t border-dashed border-[var(--chart-average)] opacity-60" />
            <span className="tnum font-mono text-[10px] text-[var(--chart-average)]">4.8432</span>
          </div>
          <svg
            viewBox="0 0 520 150"
            className="h-full w-full"
            role="img"
            aria-label={t('landing.previewChartLabel')}
          >
            <path
              d="M0 37 C42 28 72 50 110 46 C149 41 168 72 209 65 C248 58 269 84 306 79 C345 73 372 101 411 92 C449 84 476 112 520 108"
              fill="none"
              stroke="var(--chart-market)"
              strokeWidth="3"
              strokeLinecap="round"
            />
            <path
              d="M0 70 H108 V67 H209 V71 H306 V66 H411 V69 H520"
              fill="none"
              stroke="var(--chart-average)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx="520" cy="108" r="5" fill="var(--chart-market)" />
          </svg>
          <div className="absolute bottom-3 left-5 flex items-center gap-4 text-[10px] text-secondary sm:left-6">
            <span className="flex items-center gap-1.5">
              <i className="block h-0.5 w-4 bg-[var(--chart-average)]" />
              {t('landing.previewAverageShort')}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="block h-0.5 w-4 bg-[var(--chart-market)]" />
              {t('landing.previewMarketShort')}
            </span>
          </div>
        </div>

        <div className="flex items-start gap-3 p-5 sm:p-6">
          <span className="mt-1 block size-2 shrink-0 rounded-full bg-[var(--positive)] shadow-[0_0_0_5px_color-mix(in_srgb,var(--positive)_12%,transparent)]" />
          <div>
            <p className="text-[15px] font-semibold">{t('landing.previewDecision')}</p>
            <p className="mt-1 text-[13px] leading-relaxed text-secondary">
              {t('landing.previewDecisionBody')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeatureRow({
  icon,
  step,
  title,
  body,
}: {
  icon: React.ReactNode;
  step: string;
  title: string;
  body: string;
}) {
  return (
    <div className="grid grid-cols-[44px_1fr_auto] gap-4 border-b border-[var(--separator)] py-8 last:border-b-0 sm:items-center sm:py-9">
      <span
        className="flex size-11 items-center justify-center rounded-control bg-[var(--fill-3)]"
        aria-hidden
      >
        {icon}
      </span>
      <div>
        <h3 className="text-[17px] font-semibold tracking-[-0.015em]">{title}</h3>
        <p className="mt-1 max-w-xl text-[14px] leading-relaxed text-secondary sm:text-[15px]">
          {body}
        </p>
      </div>
      <span className="tnum hidden font-mono text-[12px] text-tertiary sm:block">{step}</span>
    </div>
  );
}
